import React from 'react'
import type { JSX } from 'react'

import type { MicroButtonColor } from './__internal__/micro-button-color'
import { MicroButtonIcon } from './__internal__/micro-button-icon'
import { MicroButtonText } from './__internal__/micro-button-text'

/**
 * MicroButton Component
 *
 * A compact button with purple or green variant. Compose content with
 * MicroButton.Text and optional MicroButton.Icon (12x12) as children.
 *
 * Usage:
 * <MicroButton microButtonColor={'green'} onClick={() => {}}>
 *   <MicroButton.Text>Continue</MicroButton.Text>
 * </MicroButton>
 * <MicroButton microButtonColor={'purple'}>
 *   <MicroButton.Text>Learn more</MicroButton.Text>
 *   <MicroButton.Icon iconPath={'/path/to/icon.svg'} />
 * </MicroButton>
 */
export function MicroButton({
  children,
  microButtonColor,
  ...buttonHTMLAttributes
}: {
  readonly children: React.ReactNode
  readonly microButtonColor: MicroButtonColor
} & Omit<
  JSX.IntrinsicElements['button'],
  // Omitted because not allowed:
  'className' | 'disabled' // Avoid 'disabled' since it's not accessibility friendly. Use 'aria-disabled' and 'readOnly' instead.
>) {
  return (
    <button
      className={`flex items-center justify-center cursor-pointer select-none transition-all duration-100 ease-in-out border-none shadow-none w-fit h-[26px] px-2.5 rounded-[10px] relative outline-none focus:outline-0 text-headline-2 font-demibold aria-disabled:bg-acorns-stone-light aria-disabled:text-white aria-disabled:cursor-default aria-disabled:pointer-events-none aria-disabled:border-transparent aria-disabled:[&_img]:brightness-0 aria-disabled:[&_img]:invert ${
        microButtonColor === 'purple'
          ? 'bg-acorns-purple/15 text-acorns-purple hover:bg-acorns-purple/25 [&_img]:[filter:contrast(50)_brightness(0)_saturate(100%)_invert(38%)_sepia(99%)_saturate(2600%)_hue-rotate(233deg)_brightness(86%)_contrast(103%)]'
          : 'bg-acorns-light-oak/15 text-acorns-light-oak hover:bg-acorns-light-oak/25 [&_img]:[filter:contrast(50)_brightness(0)_saturate(100%)_invert(45%)_sepia(25%)_saturate(380%)_hue-rotate(106deg)_brightness(78%)_contrast(90%)]'
      }`}
      type={'button'}
      {...buttonHTMLAttributes}
    >
      {children}
    </button>
  )
}

MicroButton.Icon = MicroButtonIcon
MicroButton.Text = MicroButtonText
